import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "../../redux/reducers";
import {
    Dialog, DialogTitle, DialogContent, DialogActions, Button, TextField,
    Table, TableHead, TableBody, TableRow, TableCell, Typography, Box, IconButton, Alert, Tooltip
} from "@mui/material";
import SaveIcon from "@mui/icons-material/Save";
import RestoreIcon from "@mui/icons-material/Restore";

import { IPayrollRates, IPayrollRateHistoryEntry, PayrollRateKey } from "../../interface/payrollShuttle";
import { getPayrollRates } from "../../tools/payrollShuttle/getPayrollRates";
import { getPayrollRateHistory } from "../../tools/payrollShuttle/getPayrollRateHistory";
import { setPayrollRate } from "../../tools/payrollShuttle/setPayrollRate";

const RATE_LABELS: Record<PayrollRateKey, string> = {
    teleworkDailyRate: "Prime télétravail (€ / jour)",
    teleworkCapDays: "Plafond télétravail (jours / mois)",
    restaurantTicketDailyRate: "Ticket restaurant (€ / jour travaillé)",
};

const RATE_KEYS = Object.keys(RATE_LABELS) as PayrollRateKey[];

interface PayrollRatesDialogProps {
    open: boolean;
    onClose: () => void;
}

export default function PayrollRatesDialog({ open, onClose }: PayrollRatesDialogProps) {
    const dispatch = useDispatch();
    const token = useSelector((state: RootState) => state.user.token);

    const [rates, setRates] = useState<IPayrollRates | null>(null);
    const [editedValues, setEditedValues] = useState<Partial<Record<PayrollRateKey, string>>>({});
    const [history, setHistory] = useState<IPayrollRateHistoryEntry[]>([]);
    const [error, setError] = useState("");

    const loadData = () => {
        if (!token) return;
        getPayrollRates(token, setRates, dispatch, setError);
        getPayrollRateHistory(token, setHistory, dispatch, setError);
    };

    useEffect(() => {
        if (open) loadData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open]);

    const handleSave = (key: PayrollRateKey) => {
        if (!token) return;
        const rawValue = editedValues[key];
        if (rawValue === undefined || rawValue === "") return;

        setPayrollRate(key, Number(rawValue), token, (updatedRates) => {
            setRates(updatedRates);
            setEditedValues((previous) => ({ ...previous, [key]: undefined }));
            getPayrollRateHistory(token, setHistory, dispatch, setError);
        }, dispatch, setError);
    };

    const handleReuse = (key: PayrollRateKey, value: number) => {
        setEditedValues((previous) => ({ ...previous, [key]: String(value) }));
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
            <DialogTitle>Taux de la navette de paie</DialogTitle>
            <DialogContent>
                {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

                <Table size="small" sx={{ mb: 3 }}>
                    <TableHead>
                        <TableRow>
                            <TableCell>Paramètre</TableCell>
                            <TableCell>Valeur actuelle</TableCell>
                            <TableCell>Nouvelle valeur</TableCell>
                            <TableCell></TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {RATE_KEYS.map((key) => (
                            <TableRow key={key}>
                                <TableCell>{RATE_LABELS[key]}</TableCell>
                                <TableCell>{rates ? rates[key] : "…"}</TableCell>
                                <TableCell>
                                    <TextField
                                        type="number"
                                        variant="standard"
                                        placeholder={rates ? String(rates[key]) : ""}
                                        value={editedValues[key] ?? ""}
                                        onChange={(e) => setEditedValues((previous) => ({ ...previous, [key]: e.target.value }))}
                                        sx={{ width: 100 }}
                                    />
                                </TableCell>
                                <TableCell>
                                    <IconButton
                                        size="small"
                                        disabled={!editedValues[key]}
                                        onClick={() => handleSave(key)}
                                    >
                                        <SaveIcon fontSize="small" />
                                    </IconButton>
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>

                <Typography variant="subtitle2" sx={{ mb: 1 }}>Historique des modifications</Typography>
                <Table size="small">
                    <TableHead>
                        <TableRow>
                            <TableCell>Paramètre</TableCell>
                            <TableCell>Ancienne valeur</TableCell>
                            <TableCell>Nouvelle valeur</TableCell>
                            <TableCell>Date</TableCell>
                            <TableCell>Modifié par</TableCell>
                            <TableCell></TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {history.length === 0 && (
                            <TableRow>
                                <TableCell colSpan={6}>
                                    <Typography variant="body2" color="text.secondary">Aucune modification enregistrée (valeurs par défaut).</Typography>
                                </TableCell>
                            </TableRow>
                        )}
                        {history.map((entry) => (
                            <TableRow key={entry._id}>
                                <TableCell>{RATE_LABELS[entry.key]}</TableCell>
                                <TableCell>{entry.previousValue}</TableCell>
                                <TableCell>{entry.value}</TableCell>
                                <TableCell>{new Date(entry.createdAt).toLocaleString("fr-FR")}</TableCell>
                                <TableCell>{entry.createdBy?.email || "—"}</TableCell>
                                <TableCell>
                                    <Tooltip title="Réutiliser l'ancienne valeur">
                                        <IconButton size="small" onClick={() => handleReuse(entry.key, entry.previousValue)}>
                                            <RestoreIcon fontSize="small" />
                                        </IconButton>
                                    </Tooltip>
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>Fermer</Button>
            </DialogActions>
        </Dialog>
    );
}
